import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Rating,
} from '@mui/material';
import {
  CheckCircle,
  Warning,
  Feedback,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import authService from '../services/auth';

const Billing = () => {
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showCancelSuccess, setShowCancelSuccess] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [rating, setRating] = useState(0);
  const [isNovalinkEmployee, setIsNovalinkEmployee] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkNovalinkEmployee = async () => {
      try {
        const response = await authService.getProfile();
        const userData = response?.data?.user || response?.user || response;
        const email = userData?.email || '';
        setIsNovalinkEmployee(email.toLowerCase().endsWith('@novalink-solutions.com'));
      } catch (error) {
        console.error('Error checking user email:', error);
      }
    };
    checkNovalinkEmployee();
  }, []);

  const handleBillingPeriodChange = (event, newPeriod) => {
    if (newPeriod !== null) {
      setBillingPeriod(newPeriod);
    }
  };

  const plans = [
    {
      name: 'Basic',
      monthlyPrice: 10,
      annualPrice: 100,
      annualSavings: 20,
      features: [
        '10 resume analyses per day',
        'Limited daily usage',
        'Basic AI matching',
        'Standard support',
        'Export results as PDF',
      ],
      description: 'Best for individuals / starters',
      popular: false,
    },
    {
      name: 'Plus',
      monthlyPrice: 15,
      annualPrice: 150,
      annualSavings: 30,
      features: [
        '20 resume analyses per day',
        'Higher daily usage than Basic',
        'Advanced AI matching',
        'Priority support',
        'Export results as PDF & DOCX',
        'Detailed analytics',
      ],
      description: 'Best for regular users',
      popular: false,
    },
    {
      name: 'Pro',
      monthlyPrice: 50,
      annualPrice: 500,
      annualSavings: 100,
      features: [
        'Unlimited resume analyses',
        'No daily limits',
        'Premium AI matching',
        '24/7 priority support',
        'All export formats',
        'Advanced analytics & insights',
        'API access',
        'Custom integrations',
      ],
      description: 'Best for teams and enterprises',
      popular: true,
    },
  ];

  const getMonthlyEquivalent = (plan) => {
    if (billingPeriod === 'annual') {
      return (plan.annualPrice / 12).toFixed(2);
    }
    return plan.monthlyPrice;
  };

  const handleGetStarted = (planName) => {
    navigate('/payment', { state: { planName } });
  };

  const handleCancelSubscription = () => {
    setShowCancelConfirm(true);
  };

  const handleConfirmCancel = () => {
    setShowCancelConfirm(false);
    // TODO: Add API call to cancel subscription here
    // Save cancellation status and date
    localStorage.setItem('subscriptionStatus', 'canceled');
    localStorage.setItem('subscriptionCanceledDate', new Date().toISOString());
    localStorage.setItem('userPlan', 'None');
    setShowCancelSuccess(true);
  };

  const handleCancelNo = () => {
    setShowCancelConfirm(false);
  };

  const handleSubmitFeedback = () => {
    // TODO: Add API call to submit feedback here
    console.log('Feedback submitted:', { feedback, rating });
    // Reload the page to update subscription status
    window.location.reload();
    navigate('/');
  };

  const handleBackToDashboard = () => {
    // Reload the page to update subscription status
    window.location.reload();
    navigate('/');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)',
        color: '#2c3e50',
        py: 8,
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              mb: 2,
              background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Upgrade to Premium
          </Typography>
        </Box>

        {/* Billing Period Toggle */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 6 }}>
          <ToggleButtonGroup
            value={billingPeriod}
            exclusive
            onChange={handleBillingPeriodChange}
            sx={{
              bgcolor: 'rgba(25, 118, 210, 0.1)',
              borderRadius: 2,
              p: 0.5,
              '& .MuiToggleButton-root': {
                color: '#1976d2',
                border: 'none',
                px: 4,
                py: 1,
                '&.Mui-selected': {
                  bgcolor: '#1976d2',
                  color: 'white',
                  '&:hover': {
                    bgcolor: '#1565c0',
                  },
                },
                '&:hover': {
                  bgcolor: 'rgba(25, 118, 210, 0.15)',
                },
              },
            }}
          >
            <ToggleButton value="annual">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1" fontWeight={600}>
                  Annual
                </Typography>
                <Chip
                  label="Best Value"
                  size="small"
                  sx={{
                    bgcolor: '#4caf50',
                    color: 'white',
                    fontSize: '0.7rem',
                    height: 20,
                    fontWeight: 600,
                  }}
                />
              </Box>
            </ToggleButton>
            <ToggleButton value="monthly">
              <Typography variant="body1" fontWeight={600}>
                Monthly
              </Typography>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Pricing Cards */}
        <Grid container spacing={4} justifyContent="center">
          {plans.map((plan, index) => (
            <Grid item xs={12} sm={6} md={4} key={plan.name}>
              <Card
                sx={{
                  height: '100%',
                  bgcolor: 'white',
                  border: plan.popular
                    ? '2px solid #1976d2'
                    : '1px solid rgba(0, 0, 0, 0.12)',
                  borderRadius: 3,
                  position: 'relative',
                  transition: 'all 0.3s ease-in-out',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: plan.popular
                      ? '0 12px 40px rgba(25, 118, 210, 0.3)'
                      : '0 12px 40px rgba(0, 0, 0, 0.15)',
                  },
                }}
              >
                {plan.popular && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      width: 32,
                      height: 32,
                      bgcolor: '#1976d2',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <CheckCircle sx={{ color: 'white', fontSize: 20 }} />
                  </Box>
                )}
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        mb: 1,
                        color: plan.popular ? '#1976d2' : '#2c3e50',
                      }}
                    >
                      {plan.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: 'rgba(44, 62, 80, 0.7)', mb: 2 }}
                    >
                      {plan.description}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1 }}>
                      <Typography
                        variant="h3"
                        sx={{
                          fontWeight: 700,
                          color: '#2c3e50',
                        }}
                      >
                        ${getMonthlyEquivalent(plan)}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: 'rgba(44, 62, 80, 0.6)',
                          ml: 1,
                        }}
                      >
                        / month
                      </Typography>
                    </Box>
                    {billingPeriod === 'annual' ? (
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ color: 'rgba(44, 62, 80, 0.7)', mb: 0.5 }}
                        >
                          ${plan.annualPrice} billed annually
                        </Typography>
                        <Chip
                          label={`SAVE ${plan.annualSavings}%`}
                          size="small"
                          sx={{
                            bgcolor: '#4caf50',
                            color: 'white',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                          }}
                        />
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{ color: 'rgba(44, 62, 80, 0.7)' }}
                      >
                        Billed monthly
                      </Typography>
                    )}
                  </Box>

                  <Divider sx={{ my: 3, bgcolor: 'rgba(0, 0, 0, 0.12)' }} />

                  <List sx={{ mb: 3 }}>
                    {plan.features.map((feature, idx) => (
                      <ListItem
                        key={idx}
                        sx={{
                          px: 0,
                          py: 0.5,
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircle
                            sx={{
                              color: '#4caf50',
                              fontSize: 20,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={feature}
                          primaryTypographyProps={{
                            variant: 'body2',
                            sx: { color: 'rgba(44, 62, 80, 0.8)' },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>

                  <Button
                    fullWidth
                    variant={plan.popular ? 'contained' : 'outlined'}
                    onClick={() => handleGetStarted(plan.name)}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '1rem',
                      bgcolor: plan.popular
                        ? 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)'
                        : 'transparent',
                      borderColor: plan.popular ? 'transparent' : '#1976d2',
                      color: plan.popular ? 'white' : '#1976d2',
                      '&:hover': {
                        bgcolor: plan.popular
                          ? 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)'
                          : 'rgba(25, 118, 210, 0.1)',
                        borderColor: plan.popular ? 'transparent' : '#1565c0',
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.2s ease-in-out',
                    }}
                  >
                    {plan.popular ? 'Get Started' : 'Upgrade'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Cancel Subscription Button */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <Button
            variant="contained"
            onClick={handleCancelSubscription}
            sx={{
              bgcolor: '#FFC107',
              color: '#2c3e50',
              py: 1.5,
              px: 4,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1rem',
              boxShadow: '0 2px 8px rgba(255, 193, 7, 0.3)',
              '&:hover': {
                bgcolor: '#FFB300',
                boxShadow: '0 4px 12px rgba(255, 193, 7, 0.4)',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            Cancel Subscription
          </Button>
        </Box>
      </Container>

      {/* Confirmation Dialog */}
      <Dialog
        open={showCancelConfirm}
        onClose={handleCancelNo}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Warning sx={{ fontSize: 32, color: '#FFC107' }} />
            <Typography variant="h6" fontWeight={700}>
              Cancel Subscription
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ color: 'rgba(44, 62, 80, 0.8)' }}>
            Do you really want to cancel your subscription? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={handleCancelNo}
            variant="outlined"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
            }}
          >
            No
          </Button>
          <Button
            onClick={handleConfirmCancel}
            variant="contained"
            sx={{
              bgcolor: '#FFC107',
              color: '#2c3e50',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              '&:hover': {
                bgcolor: '#FFB300',
              },
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Dialog with Feedback */}
      <Dialog
        open={showCancelSuccess}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: '#4caf50',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <CheckCircle sx={{ fontSize: 48, color: 'white' }} />
            </Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                mb: 2,
                color: '#2c3e50',
              }}
            >
              You have cancelled the subscription
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'rgba(44, 62, 80, 0.7)',
                mb: 4,
              }}
            >
              We're sorry to see you go. Your subscription will remain active until the end of your billing period.
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Feedback Section */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Feedback sx={{ color: '#1976d2' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                We'd love your feedback
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{ color: 'rgba(44, 62, 80, 0.7)', mb: 2 }}
            >
              Please let us know why you're cancelling. Your feedback helps us improve.
            </Typography>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1, color: 'rgba(44, 62, 80, 0.7)' }}>
                Rate your experience
              </Typography>
              <Rating
                value={rating}
                onChange={(event, newValue) => {
                  setRating(newValue);
                }}
                size="large"
              />
            </Box>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Tell us what we could improve..."
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: '#1976d2',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1976d2',
                  },
                },
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleBackToDashboard}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                py: 1.5,
                borderColor: '#1976d2',
                color: '#1976d2',
                '&:hover': {
                  borderColor: '#1565c0',
                  bgcolor: 'rgba(25, 118, 210, 0.1)',
                },
              }}
            >
              Back to Dashboard
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSubmitFeedback}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                py: 1.5,
                bgcolor: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                color: 'white',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                },
              }}
            >
              Submit Feedback
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Billing;
